import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Library, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import MediaLibraryModal from "./admin/MediaLibraryModal";
import { useUploadStatus } from "../context/UploadStatusContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function GalleryImagesField({ value, onChange, label = "More Photos", testIdPrefix = "gallery" }) {
    const images = Array.isArray(value) ? value : [];
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [libraryOpen, setLibraryOpen] = useState(false);
    const { begin: beginUpload, end: endUpload } = useUploadStatus();

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        beginUpload();
        const urls = [];
        for (const file of files) {
            if (!file.type.startsWith("image/")) {
                toast.error(`${file.name} is not an image`);
                continue;
            }
            if (file.size > 8 * 1024 * 1024) {
                toast.error(`${file.name} is over 8 MB`);
                continue;
            }
            try {
                const fd = new FormData();
                fd.append("file", file);
                const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                urls.push(r.data.url.startsWith("http") ? r.data.url : `${BACKEND_URL}${r.data.url}`);
            } catch (err) {
                toast.error(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
            }
        }
        if (urls.length) {
            onChange([...images, ...urls]);
            toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added. Click Save to publish.`);
        }
        setUploading(false);
        endUpload();
        if (inputRef.current) inputRef.current.value = "";
    };

    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= images.length) return;
        const next = [...images];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };

    return (
        <div data-testid={`${testIdPrefix}-field`}>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-1">{label}</label>
            <p className="text-xs text-ink-soft mb-3">
                Extra photos of the same product. Customers see them as thumbnails and can scroll through with arrows. The main photo above always comes first.
            </p>

            {images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3">
                    {images.map((url, i) => (
                        <div key={`${url}-${i}`} className="relative group aspect-square overflow-hidden bg-[#F9F5F2] border border-[#EAE3DE]">
                            <img src={url} alt={`Photo ${i + 2}`} className="w-full h-full object-cover" loading="lazy" />
                            <span className="absolute top-1 left-1 text-[9px] bg-black/55 text-white px-1 rounded">{i + 2}</span>
                            <button
                                type="button"
                                onClick={() => onChange(images.filter((_, k) => k !== i))}
                                className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white flex items-center justify-center hover:bg-red-500 rounded-sm"
                                aria-label={`Remove photo ${i + 2}`}
                                data-testid={`${testIdPrefix}-remove-${i}`}
                            >
                                <X size={12} />
                            </button>
                            <div className="absolute bottom-1 left-1 right-1 flex justify-between">
                                <button
                                    type="button"
                                    onClick={() => move(i, -1)}
                                    disabled={i === 0}
                                    className="w-5 h-5 bg-white/85 text-ink flex items-center justify-center rounded-sm disabled:opacity-30"
                                    aria-label="Move earlier"
                                >
                                    <ChevronLeft size={12} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => move(i, 1)}
                                    disabled={i === images.length - 1}
                                    className="w-5 h-5 bg-white/85 text-ink flex items-center justify-center rounded-sm disabled:opacity-30"
                                    aria-label="Move later"
                                >
                                    <ChevronRight size={12} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" onChange={onFiles} className="hidden" data-testid={`${testIdPrefix}-file-input`} />
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-ink text-white text-[10px] tracking-[0.2em] uppercase hover:bg-blush transition-colors disabled:opacity-60"
                    data-testid={`${testIdPrefix}-upload-btn`}
                >
                    <Upload size={14} /> {uploading ? "Uploading…" : "Add photos"}
                </button>
                <button
                    type="button"
                    onClick={() => setLibraryOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-3 border border-[#EAE3DE] text-ink text-[10px] tracking-[0.2em] uppercase hover:border-blush hover:text-blush transition-colors"
                    data-testid={`${testIdPrefix}-library-btn`}
                >
                    <Library size={14} /> From library
                </button>
            </div>

            {libraryOpen && (
                <MediaLibraryModal
                    onSelect={(url) => {
                        onChange([...images, url]);
                        setLibraryOpen(false);
                    }}
                    onClose={() => setLibraryOpen(false)}
                />
            )}
        </div>
    );
}
